import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import 'iconify-icon';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import ShareButtons from '../components/ShareButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const { data } = await axios.get(`${API_URL}/public/blog/${slug}`);
        setPost(data);
        try {
          const { data: rel } = await axios.get(`${API_URL}/public/blog`, { params: { category: data.category } });
          setRelated(rel.filter((p) => p.slug !== slug).slice(0, 3));
        } catch {
          setRelated([]);
        }
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-orange"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="bg-black min-h-screen">
        <Header />
        <main className="pt-32 pb-24 min-h-[60vh] text-center max-w-2xl mx-auto px-6">
          <iconify-icon icon="solar:document-text-linear" className="text-6xl text-neutral-600 mb-4"></iconify-icon>
          <h1 className="text-3xl font-bold mb-3 text-[#EAEAEA]">Artigo não encontrado</h1>
          <p className="text-neutral-500 mb-8">O artigo que você procura pode ter sido removido ou movido.</p>
          <Link to="/blog" className="inline-flex items-center gap-2 px-6 py-3 bg-accent-orange text-white rounded-full text-sm font-mono uppercase tracking-wider hover:bg-[#E55D00] transition-colors">
            <iconify-icon icon="solar:arrow-left-linear"></iconify-icon>
            Voltar ao Blog
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const formattedDate = new Date(post.published_at || post.created_at).toLocaleDateString('pt-BR', {
    day: '2-digit', month: 'long', year: 'numeric',
  });

  return (
    <div data-testid="blog-post-page" className="bg-black min-h-screen">
      <SEO
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.featured_image}
        type="article"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          image: post.featured_image,
          datePublished: post.published_at || post.created_at,
          dateModified: post.updated_at || post.created_at,
          author: { '@type': 'Organization', name: 'Instituto Eneagrama' },
          publisher: {
            '@type': 'Organization',
            name: 'Instituto Eneagrama',
            logo: { '@type': 'ImageObject', url: 'https://customer-assets.emergentagent.com/job_b2005392-6212-4506-b62b-d5c77af4b77a/artifacts/n8p0qeqa_Logomarca_Horizontal_Preta.png' },
          },
          articleSection: post.category,
        }}
      />
      <Header />
      <main className="pt-20">
        {/* Cover image hero with gradient overlay */}
        {post.featured_image ? (
          <section className="relative h-[55vh] min-h-[420px] max-h-[640px] overflow-hidden">
            <img src={post.featured_image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/40"></div>
            <div className="relative z-10 h-full flex flex-col justify-end max-w-3xl mx-auto px-6 md:px-12 pb-12">
              <Link to="/blog" className="self-start inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/60 hover:text-accent-orange transition-colors mb-6">
                <iconify-icon icon="solar:arrow-left-linear"></iconify-icon>
                Voltar ao Blog
              </Link>
              <div className="flex flex-wrap items-center gap-3 text-xs mb-4">
                <span className="px-3 py-1 bg-accent-orange text-white rounded-full font-mono uppercase tracking-widest">
                  {post.category}
                </span>
                <span className="text-white/60 flex items-center gap-1.5 font-mono uppercase tracking-widest">
                  <iconify-icon icon="solar:calendar-linear"></iconify-icon>
                  {formattedDate}
                </span>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-white leading-[1.15]">
                {post.title}
              </h1>
            </div>
          </section>
        ) : (
          <section className="relative pt-12 pb-10 border-b border-neutral-900" style={{ background: 'rgba(10, 10, 10, 0.78)' }}>
            <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-12">
              <Link to="/blog" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-accent-orange transition-colors mb-6">
                <iconify-icon icon="solar:arrow-left-linear"></iconify-icon>
                Voltar ao Blog
              </Link>
              <div className="flex flex-wrap items-center gap-3 text-xs mb-5">
                <span className="px-3 py-1 bg-accent-orange text-white rounded-full font-mono uppercase tracking-widest">
                  {post.category}
                </span>
                <span className="text-neutral-400 flex items-center gap-1.5 font-mono uppercase tracking-widest">
                  <iconify-icon icon="solar:calendar-linear"></iconify-icon>
                  {formattedDate}
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-[#EAEAEA] leading-[1.15]">
                {post.title}
              </h1>
            </div>
          </section>
        )}

        {/* Article container — delimited reading column */}
        <section
          className="relative z-10 py-16 md:py-20"
          style={{ background: 'rgba(8, 8, 8, 0.85)' }}
        >
          <div className="relative z-10 max-w-[780px] mx-auto px-6">
            <article
              data-testid="blog-post-article"
              className="rounded-2xl border border-[#222222] bg-[#0D0D0D] p-7 md:p-12 shadow-2xl shadow-black/40"
            >
              {/* Excerpt as a lede */}
              {post.excerpt && (
                <p className="text-lg md:text-xl text-neutral-300 leading-[1.8] mb-8 pb-8 border-b border-[#222222] font-medium">
                  {post.excerpt}
                </p>
              )}

              {/* Body — 720px reading width, 18px / line-height 1.8 */}
              <div
                className="text-neutral-300 text-[17px] md:text-[18px] leading-[1.8] [&>p]:mb-6 [&>h2]:text-2xl md:[&>h2]:text-3xl [&>h2]:font-bold [&>h2]:mt-12 [&>h2]:mb-5 [&>h2]:text-white [&>h2]:leading-tight [&>h3]:text-xl md:[&>h3]:text-2xl [&>h3]:font-semibold [&>h3]:mt-10 [&>h3]:mb-4 [&>h3]:text-white [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ul]:space-y-2 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>ol]:space-y-2 [&_a]:underline [&_a]:text-accent-orange [&_a]:underline-offset-4 [&>blockquote]:border-l-4 [&>blockquote]:border-accent-orange [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:text-neutral-400 [&>blockquote]:my-8 [&>blockquote]:py-2 [&>strong]:text-white [&>strong]:font-semibold [&>img]:rounded-xl [&>img]:my-8"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content || '') }}
              />

              {/* Tags */}
              {post.tags?.length > 0 && (
                <div className="mt-10 pt-8 border-t border-[#222222]">
                  <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-accent-orange mb-3">
                    Tags
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {post.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 bg-[#1A1A1A] border border-[#262626] text-neutral-400 rounded-full text-xs font-mono">
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Share */}
              <div className="mt-10 pt-8 border-t border-[#222222]">
                <ShareButtons url={`/blog/${post.slug}`} title={post.title} description={post.excerpt} />
              </div>
            </article>
          </div>
        </section>

        {/* Related — "Leia também" */}
        {related.length > 0 && (
          <section
            className="relative z-10 py-20 border-t border-neutral-900"
            style={{ background: 'rgba(17, 17, 17, 0.85)' }}
          >
            <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12">
              <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-3 block">
                Leia também
              </span>
              <h3 className="text-2xl md:text-3xl font-bold text-[#EAEAEA] mb-10">
                Continue a leitura
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {related.map((r) => (
                  <Link
                    to={`/blog/${r.slug}`}
                    key={r.post_id}
                    className="group bg-[#0F0F0F] border border-[#222222] rounded-2xl overflow-hidden hover:border-accent-orange/60 transition-all block"
                    data-testid={`related-post-${r.slug}`}
                  >
                    {r.featured_image && (
                      <div className="relative aspect-video bg-[#1F1F1F] overflow-hidden">
                        <img src={r.featured_image} alt={r.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                      </div>
                    )}
                    <div className="p-6">
                      <div className="text-[10px] text-accent-orange font-mono uppercase tracking-widest mb-2.5">{r.category}</div>
                      <h4 className="font-bold text-[#EAEAEA] mb-2 line-clamp-2 leading-tight group-hover:text-accent-orange transition-colors">
                        {r.title}
                      </h4>
                      <p className="text-sm text-neutral-500 line-clamp-2 leading-[1.7]">{r.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;
